import { useEffect, useRef, useState } from "react";
import { EventEmitter } from "../../utils/event-emitter";
import Events from "../../../const/events";
import ContactApi from "../../../api/contact-api";
import OutsideClickHandler from "react-outside-click-handler";
import { QRCodeSVG } from "qrcode.react";
import "./qr-viewer.scss";

const QRViewer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [qrData, setQrData] = useState("");
  const [contactId, setContactId] = useState("");
  const [contactName, setContactName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const qrContainerRef = useRef(null);

  const _loadQRCode = async (contactId) => {
    try {
      const response = await ContactApi.private.getRoomLink(contactId);
      setQrData(response?.data?.data);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setError("Failed to load QR. Please try again.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    EventEmitter.subscribe(Events.SHOW_QR_CODE_POPUP, ({ id, title }) => {
      // Prepare modal immediately for better perceived performance
      setContactId(id);
      setContactName(title);
      setError("");
      setQrData("");
      setIsVisible(true);
      setIsLoading(true);
      _loadQRCode(id);
    });
    return () => {
      EventEmitter.cancelAll(Events.SHOW_QR_CODE_POPUP);
    };
  }, []);

  if (!isVisible) {
    return null;
  }

  const closeModal = () => {
    setIsVisible(false);
    setIsLoading(false);
    setQrData("");
    setError("");
  };

  const copyLink = async () => {
    if (!qrData) return;
    try {
      await navigator.clipboard.writeText(qrData);
    } catch (e) {
      // Fallback
      const tmp = document.createElement("textarea");
      tmp.value = qrData;
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand("copy");
      document.body.removeChild(tmp);
    }
  };

  const downloadSvg = () => {
    try {
      const container = qrContainerRef.current;
      if (!container) return;
      const svg = container.querySelector("svg");
      if (!svg) return;
      const serializer = new XMLSerializer();
      const source = serializer.serializeToString(svg);
      const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `relai-qr-${contactName || "contact"}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download QR svg", e);
    }
  };

  return (
    <div className="modal-wrapper qr-viewer">
      <div
        className="modal fade show"
        id="newMessageModal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="newMessageModalTitle"
        aria-hidden="false"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <OutsideClickHandler
            onOutsideClick={() => {
              closeModal();
            }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h6
                  className="modal-title m-0 d-flex align-items-center gap-2"
                  id="newMessageModalTitle"
                >
                  <i className="mdi mdi-qrcode-scan" /> Quick Connect
                  <span className="title-sub text-muted">to {contactName}</span>
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                {/* Content */}
                <div className="qr-content">
                  <div className="qr-pane">
                    <div className="qr-frame">
                      {isLoading ? (
                        <div className="qr-skeleton" aria-label="Loading QR" />
                      ) : error ? (
                        <div className="qr-error text-center">
                          <i className="mdi mdi-alert-circle-outline" /> {error}
                        </div>
                      ) : (
                        <div ref={qrContainerRef} className="qr-box p-3">
                          <QRCodeSVG
                            value={qrData}
                            size={240}
                            level={"H"}
                            includeMargin={true}
                          />
                        </div>
                      )}
                    </div>
                    {!isLoading && !error && qrData && (
                      <div className="qr-caption">Scan to start a secure chat</div>
                    )}
                  </div>
                  <div className="actions-pane">
                    <label className="form-label">Shareable link</label>
                    <div className="input-group mb-2">
                      <input
                        type="text"
                        className="form-control"
                        value={qrData || (isLoading ? "Fetching link…" : "")}
                        readOnly
                      />
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        className="bouncy-button white small"
                        type="button"
                        onClick={copyLink}
                        disabled={!qrData}
                        title="Copy link"
                      >
                        <i className="mdi mdi-content-copy" />
                        <span>Copy</span>
                      </button>
                      <a
                        className={`bouncy-button small ${qrData ? "" : "disabled"}`}
                        href={qrData || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open link"
                        aria-disabled={!qrData}
                        onClick={(e) => {
                          if (!qrData) e.preventDefault();
                        }}
                      >
                        <i className="mdi mdi-open-in-new" />
                        <span>Open</span>
                      </a>
                      <button
                        className="bouncy-button secondary small"
                        type="button"
                        onClick={downloadSvg}
                        disabled={!qrData}
                        title="Download QR"
                      >
                        <i className="mdi mdi-download" />
                        <span>Download</span>
                      </button>
                    </div>
                    <div className="helper-text">
                      Anyone with this link or QR can start a conversation.
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer d-flex align-items-center justify-content-end">
                <button type="button" className="btn" data-bs-dismiss="modal" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </OutsideClickHandler>
        </div>
      </div>
    </div>
  );
};

export default QRViewer;
